/**
 * @fileoverview Matchday Revenue page — per-match income tracking
 * Covers tickets, hospitality, F&B, merchandise, broadcasting, and prize money.
 * BRD F-11: All revenue streams tracked independently for AIFF financial reporting.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftProgress from "components/SoftProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";
import { useAuth } from "context/AuthContext";

// ── Constants ────────────────────────────────────────────────────────────────
const COMPETITIONS = ["ISL", "Super Cup", "Durand Cup", "AFC Cup", "Friendly", "Other"];
const VENUES = ["Home", "Away", "Neutral"];

const EMPTY_FORM = {
  matchDate: "", opponent: "", competition: "ISL", venue: "Home",
  stadiumName: "", currency: "INR",
  ticketRevenue: "0", hospitalityRevenue: "0", foodBeverageRevenue: "0",
  merchandiseRevenue: "0", broadcastingShare: "0", prizeMoney: "0", otherRevenue: "0",
  attendance: "", averageTicketPrice: "", notes: "",
};

function mapDto(dto) {
  return {
    id: dto.id, matchDate: dto.matchDate, opponent: dto.opponent,
    competition: dto.competition, venue: dto.venue, stadiumName: dto.stadiumName,
    ticketRevenue: dto.ticketRevenue, hospitalityRevenue: dto.hospitalityRevenue,
    foodBeverageRevenue: dto.foodBeverageRevenue, merchandiseRevenue: dto.merchandiseRevenue,
    broadcastingShare: dto.broadcastingShare, prizeMoney: dto.prizeMoney,
    otherRevenue: dto.otherRevenue, totalRevenue: dto.totalRevenue,
    currency: dto.currency, attendance: dto.attendance,
    averageTicketPrice: dto.averageTicketPrice, notes: dto.notes,
  };
}

// ── Inline revenue row for form ─────────────────────────────────────────────
function RevenueField({ label, fieldKey, value, onChange }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <TextField fullWidth label={label} type="number" value={value}
        onChange={(e) => onChange(fieldKey, e.target.value)} size="small"
        inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }}
        helperText={value && Number(value) > 0 ? formatCompact(Number(value)) : ""} />
    </Grid>
  );
}

RevenueField.propTypes = {
  label: PropTypes.string.isRequired,
  fieldKey: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// ── Component ─────────────────────────────────────────────────────────────────
function MatchdayRevenuePage() {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [competitionFilter, setCompetitionFilter] = useState("All");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [deleteId, setDeleteId] = useState(null);

  const fetchRecords = useCallback(() => {
    setLoading(true);
    financeService.getMatchdayRevenue()
      .then((data) => setRecords(data.map(mapDto)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const totalRevenue = records.reduce((s, r) => s + r.totalRevenue, 0);
  const homeMatches = records.filter((r) => r.venue === "Home");
  const totalTickets = records.reduce((s, r) => s + r.ticketRevenue, 0);
  const totalBroadcasting = records.reduce((s, r) => s + r.broadcastingShare, 0);
  const avgMatchRevenue = records.length > 0 ? totalRevenue / records.length : 0;
  const totalAttendance = homeMatches.reduce((s, r) => s + (r.attendance || 0), 0);

  // ── Filtered ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => records.filter((r) => {
    const matchSearch = r.opponent.toLowerCase().includes(search.toLowerCase());
    const matchComp = competitionFilter === "All" || r.competition === competitionFilter;
    return matchSearch && matchComp;
  }), [records, search, competitionFilter]);

  // Revenue stream breakdown for summary
  const streamTotals = useMemo(() => ({
    Tickets: records.reduce((s, r) => s + r.ticketRevenue, 0),
    Hospitality: records.reduce((s, r) => s + r.hospitalityRevenue, 0),
    "F&B": records.reduce((s, r) => s + r.foodBeverageRevenue, 0),
    Merchandise: records.reduce((s, r) => s + r.merchandiseRevenue, 0),
    Broadcasting: records.reduce((s, r) => s + r.broadcastingShare, 0),
    "Prize Money": records.reduce((s, r) => s + r.prizeMoney, 0),
    Other: records.reduce((s, r) => s + r.otherRevenue, 0),
  }), [records]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM); setFormErrors({}); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      matchDate: r.matchDate, opponent: r.opponent, competition: r.competition,
      venue: r.venue, stadiumName: r.stadiumName || "", currency: r.currency,
      ticketRevenue: String(r.ticketRevenue), hospitalityRevenue: String(r.hospitalityRevenue),
      foodBeverageRevenue: String(r.foodBeverageRevenue), merchandiseRevenue: String(r.merchandiseRevenue),
      broadcastingShare: String(r.broadcastingShare), prizeMoney: String(r.prizeMoney),
      otherRevenue: String(r.otherRevenue), attendance: r.attendance ? String(r.attendance) : "",
      averageTicketPrice: r.averageTicketPrice ? String(r.averageTicketPrice) : "", notes: r.notes || "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const fc = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const previewTotal = Object.fromEntries(
    ["ticketRevenue", "hospitalityRevenue", "foodBeverageRevenue", "merchandiseRevenue",
     "broadcastingShare", "prizeMoney", "otherRevenue"]
      .map((k) => [k, Number(form[k]) || 0])
  );
  const formTotal = Object.values(previewTotal).reduce((a, b) => a + b, 0);

  const validate = () => {
    const e = {};
    if (!form.matchDate) e.matchDate = "Required";
    if (!form.opponent.trim()) e.opponent = "Required";
    if (!form.competition) e.competition = "Required";
    if (!form.venue) e.venue = "Required";
    return e;
  };

  const handleSave = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setLoading(true);
    try {
      const payload = {
        matchDate: form.matchDate, opponent: form.opponent, competition: form.competition,
        venue: form.venue, stadiumName: form.stadiumName || null, currency: form.currency,
        ticketRevenue: Number(form.ticketRevenue) || 0,
        hospitalityRevenue: Number(form.hospitalityRevenue) || 0,
        foodBeverageRevenue: Number(form.foodBeverageRevenue) || 0,
        merchandiseRevenue: Number(form.merchandiseRevenue) || 0,
        broadcastingShare: Number(form.broadcastingShare) || 0,
        prizeMoney: Number(form.prizeMoney) || 0,
        otherRevenue: Number(form.otherRevenue) || 0,
        attendance: form.attendance ? Number(form.attendance) : null,
        averageTicketPrice: form.averageTicketPrice ? Number(form.averageTicketPrice) : null,
        notes: form.notes || null, tenantId: currentUser.tenantId,
      };
      if (editId) {
        await financeService.updateMatchdayRevenue(editId, payload);
      } else {
        await financeService.createMatchdayRevenue(payload);
      }
      await fetchRecords();
      setDialogOpen(false);
    } catch (err) {
      setFormErrors({ submit: err?.title ?? err?.message ?? "Save failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await financeService.deleteMatchdayRevenue(deleteId);
      await fetchRecords();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* Header */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Matchday Revenue</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Per-match income across all streams · {CLUB_NAME}
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="finance.edit">
            <SoftButton variant="gradient" color="info" onClick={openAdd}>
              <Icon sx={{ mr: 0.5 }}>add</Icon>Add Match
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Total Matchday Revenue" }} count={formatCompact(totalRevenue)}
                percentage={{ color: "success", text: `${records.length} matches recorded` }}
                icon={{ color: "success", component: "stadium" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Avg Revenue/Match" }} count={formatCompact(avgMatchRevenue)}
                percentage={{ color: "info", text: "per game average" }}
                icon={{ color: "info", component: "bar_chart" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Broadcasting Total" }} count={formatCompact(totalBroadcasting)}
                percentage={{ color: "primary", text: "FSDL + OTT distribution" }}
                icon={{ color: "primary", component: "live_tv" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Total Attendance" }} count={totalAttendance.toLocaleString("en-IN")}
                percentage={{ color: "warning", text: `${homeMatches.length} home matches` }}
                icon={{ color: "warning", component: "people" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Revenue stream breakdown */}
        {records.length > 0 && (
          <SoftBox mb={3}>
            <Card>
              <SoftBox p={3}>
                <SoftTypography variant="h6" fontWeight="medium" mb={2}>Revenue Stream Breakdown</SoftTypography>
                <Grid container spacing={2}>
                  {Object.entries(streamTotals).map(([name, val]) => {
                    const pct = totalRevenue > 0 ? Math.round((val / totalRevenue) * 100) : 0;
                    if (val === 0) return null;
                    return (
                      <Grid key={name} item xs={12} sm={6} md={4}>
                        <SoftBox mb={1}>
                          <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                            <SoftTypography variant="caption" fontWeight="medium">{name}</SoftTypography>
                            <SoftTypography variant="caption" color="text">{formatCompact(val)} ({pct}%)</SoftTypography>
                          </SoftBox>
                          <SoftProgress value={pct} color="info" />
                        </SoftBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </SoftBox>
            </Card>
          </SoftBox>
        )}

        {/* Table */}
        <Card>
          <SoftBox p={3}>
            <Grid container spacing={2} mb={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField fullWidth placeholder="Search opponent…" value={search}
                  onChange={(e) => setSearch(e.target.value)} size="small"
                  InputProps={{ startAdornment: <Icon sx={{ mr: 1, color: "#aaa" }}>search</Icon> }} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Competition</InputLabel>
                  <Select value={competitionFilter} label="Competition" onChange={(e) => setCompetitionFilter(e.target.value)}>
                    <MenuItem value="All">All Competitions</MenuItem>
                    {COMPETITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4}>
                <SoftTypography variant="caption" color="text">{filtered.length} of {records.length} matches</SoftTypography>
              </Grid>
            </Grid>

            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Date", "Opponent", "Competition", "Venue", "Tickets", "Broadcasting", "Hospitality", "Merchandise", "Other", "Total", "Attendance", "Actions"].map((h) => (
                      <SoftBox key={h} component="th" sx={{
                        textAlign: "left", p: 1.5, color: "text.secondary",
                        fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase",
                        borderBottom: "2px solid #e9ecef", whiteSpace: "nowrap",
                      }}>{h}</SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {loading ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={12}><FootballLoader label="Loading matchday revenue…" /></SoftBox>
                    </SoftBox>
                  ) : filtered.length === 0 ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={12} sx={{ textAlign: "center", p: 4 }}>
                        <SoftTypography variant="caption" color="text">No matchday records found.</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ) : filtered.map((r, i) => (
                    <SoftBox key={r.id} component="tr" sx={{
                      "&:hover": { backgroundColor: "#f8f9fa" },
                      backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                    }}>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                        <SoftTypography variant="caption" color="text">{formatDateIN(r.matchDate)}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="medium">{r.opponent}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBadge badgeContent={r.competition} color="info" size="xs" container />
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBadge badgeContent={r.venue}
                          color={r.venue === "Home" ? "success" : r.venue === "Away" ? "secondary" : "warning"}
                          size="xs" container />
                      </SoftBox>
                      {["ticketRevenue", "broadcastingShare", "hospitalityRevenue", "merchandiseRevenue"].map((key) => (
                        <SoftBox key={key} component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color={r[key] > 0 ? "dark" : "text"}>
                            {r[key] > 0 ? formatCompact(r[key]) : "—"}
                          </SoftTypography>
                        </SoftBox>
                      ))}
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <Tooltip title={`F&B: ${formatCompact(r.foodBeverageRevenue)} · Prize: ${formatCompact(r.prizeMoney)} · Other: ${formatCompact(r.otherRevenue)}`}>
                          <SoftTypography variant="caption" color="text">
                            {formatCompact(r.foodBeverageRevenue + r.prizeMoney + r.otherRevenue)}
                          </SoftTypography>
                        </Tooltip>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="bold" color="dark">
                          {formatCompact(r.totalRevenue)}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="caption" color="text">
                          {r.attendance ? r.attendance.toLocaleString("en-IN") : "—"}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <PermissionGate permission="finance.edit">
                          <SoftBox display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <SoftButton variant="text" color="info" size="small" onClick={() => openEdit(r)} sx={{ minWidth: 0, p: 0.5 }}>
                                <Icon>edit</Icon>
                              </SoftButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <SoftButton variant="text" color="error" size="small" onClick={() => setDeleteId(r.id)} sx={{ minWidth: 0, p: 0.5 }}>
                                <Icon>delete</Icon>
                              </SoftButton>
                            </Tooltip>
                          </SoftBox>
                        </PermissionGate>
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Add / Edit Dialog ────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {editId ? "Edit Match Revenue" : "Record Match Revenue"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Match Date" required type="date" value={form.matchDate}
                onChange={(e) => fc("matchDate", e.target.value)}
                error={!!formErrors.matchDate} helperText={formErrors.matchDate} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={5}>
              <TextField fullWidth label="Opponent" required value={form.opponent}
                onChange={(e) => fc("opponent", e.target.value)}
                error={!!formErrors.opponent} helperText={formErrors.opponent} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small" error={!!formErrors.competition}>
                <InputLabel shrink>Competition</InputLabel>
                <Select value={form.competition} label="Competition" onChange={(e) => fc("competition", e.target.value)}>
                  {COMPETITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small" error={!!formErrors.venue}>
                <InputLabel shrink>Venue</InputLabel>
                <Select value={form.venue} label="Venue" onChange={(e) => fc("venue", e.target.value)}>
                  {VENUES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Stadium Name" value={form.stadiumName}
                onChange={(e) => fc("stadiumName", e.target.value)} size="small"
                placeholder="e.g. Salt Lake Stadium" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Attendance" type="number" value={form.attendance}
                onChange={(e) => fc("attendance", e.target.value)} size="small"
                inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Currency</InputLabel>
                <Select value={form.currency} label="Currency" onChange={(e) => fc("currency", e.target.value)}>
                  {["INR", "USD"].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Revenue streams */}
            <Grid item xs={12}>
              <SoftTypography variant="caption" fontWeight="bold" color="secondary" textTransform="uppercase">
                Revenue Streams
              </SoftTypography>
            </Grid>
            <RevenueField label="Ticket Revenue" fieldKey="ticketRevenue" value={form.ticketRevenue} onChange={fc} />
            <RevenueField label="Hospitality" fieldKey="hospitalityRevenue" value={form.hospitalityRevenue} onChange={fc} />
            <RevenueField label="Food & Beverage" fieldKey="foodBeverageRevenue" value={form.foodBeverageRevenue} onChange={fc} />
            <RevenueField label="Merchandise" fieldKey="merchandiseRevenue" value={form.merchandiseRevenue} onChange={fc} />
            <RevenueField label="Broadcasting Share" fieldKey="broadcastingShare" value={form.broadcastingShare} onChange={fc} />
            <RevenueField label="Prize Money" fieldKey="prizeMoney" value={form.prizeMoney} onChange={fc} />
            <RevenueField label="Other Revenue" fieldKey="otherRevenue" value={form.otherRevenue} onChange={fc} />

            {/* Preview total */}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Total (preview)"
                value={formTotal > 0 ? formatCompact(formTotal) : "₹0"}
                size="small" InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f9fa", fontWeight: 700 } }}
                InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={form.notes}
                onChange={(e) => fc("notes", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            {formErrors.submit && (
              <Grid item xs={12}><Alert severity="error">{formErrors.submit}</Alert></Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave}>
            {editId ? "Update" : "Record"} Revenue
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Delete Match Revenue</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Are you sure you want to delete this matchday revenue record? This cannot be undone.
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDeleteId(null)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleDelete}>Delete</SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MatchdayRevenuePage;
